import { useAppDispatch } from 'app/hooks';
import { MIN_SPACE_DIM_FEET, STRUCTURE_GRID_SIZE } from 'data/constants';
import {
  FootprintPolygonsType,
  GridSpaceDataType,
  PartialWithRequired,
  PositionType,
  SpaceType
} from 'data/types';
import {
  beginUndoBatch,
  endUndoBatch,
  updateStructureItem,
  updateStructureItemAttribute
} from 'features/structure';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  AngledEdgeDragAxis,
  angledEdgeDragAxis,
  assignSpacePolygonShape,
  boundingBoxOfVertices,
  buildOtherSpacePolygonsAbsolute,
  EdgeCardinal,
  getBboxCornerForVertex,
  isEdgeAxisAligned,
  isPolygonInsideAnyFootprint,
  isPolygonSimple,
  isRectangularVertices,
  polygonArea,
  polygonOverlapsAny,
  RelativeVertex,
  verticesToAbsolute
} from 'utils/space-polygon';
import {
  AngledEdgeDragSession,
  PolygonDragSession,
  PolygonHandleActiveState,
  PolygonHandleHoverState,
  VertexDragSession
} from './polygon-handles-types';
import {
  buildVertexCandidate,
  ClampedDelta,
  findMaxValidEdgeDelta,
  findMaxValidVertexDelta,
  findNearestNeighborVertexFeet
} from './use-polygon-handle-drag-helpers';

type UsePolygonHandleDragParams = {
  gridSpace: GridSpaceDataType;
  allGridSpaces: GridSpaceDataType[];
  footprints: FootprintPolygonsType | null;
  effectiveZoom: number;
  beginCornerResize: (
    e: React.MouseEvent,
    handle: 'nw' | 'ne' | 'sw' | 'se'
  ) => void;
  beginEdgeResize: (e: React.MouseEvent, cardinal: EdgeCardinal) => void;
};

type UsePolygonHandleDragResult = {
  onVertexMouseDown: (e: React.MouseEvent, vertexIndex: number) => void;
  onEdgeMouseDown: (e: React.MouseEvent, edgeIndex: number) => void;
  hoverState: PolygonHandleHoverState;
  activeState: PolygonHandleActiveState;
  setHoverVertex: (vertexIndex: number | null) => void;
  setHoverEdge: (edgeIndex: number | null) => void;
};

function pixelVerticesToFeet(
  verticesPixels: RelativeVertex[]
): RelativeVertex[] {
  return verticesPixels.map((v) => ({
    x: v.x / STRUCTURE_GRID_SIZE,
    y: v.y / STRUCTURE_GRID_SIZE
  }));
}

function pointInside(p: PositionType, polygon: PositionType[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i, i += 1) {
    const xi = polygon[i].x;
    const yi = polygon[i].y;
    const xj = polygon[j].x;
    const yj = polygon[j].y;
    const intersect =
      yi > p.y !== yj > p.y && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}

function getCardinalFromEdge(
  vertices: RelativeVertex[],
  edgeIndex: number
): EdgeCardinal | null {
  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % vertices.length];
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const eps = 1e-3;
  if (Math.abs(dy) < eps) {
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    const probeUp = { x: midX, y: midY - eps };
    const isInside = vertices.length >= 3 && pointInside(probeUp, vertices);
    return isInside ? 'bottom' : 'top';
  }
  if (Math.abs(dx) < eps) {
    const midX = (p1.x + p2.x) / 2;
    const midY = (p1.y + p2.y) / 2;
    const probeLeft = { x: midX - eps, y: midY };
    const isInside = vertices.length >= 3 && pointInside(probeLeft, vertices);
    return isInside ? 'right' : 'left';
  }
  return null;
}

function edgeOrientationAxis(
  vertices: RelativeVertex[],
  edgeIndex: number
): AngledEdgeDragAxis {
  const p1 = vertices[edgeIndex];
  const p2 = vertices[(edgeIndex + 1) % vertices.length];
  return Math.abs(p1.y - p2.y) < 1e-3 ? 'vertical' : 'horizontal';
}

export function usePolygonHandleDrag({
  gridSpace,
  allGridSpaces,
  footprints,
  effectiveZoom,
  beginCornerResize,
  beginEdgeResize
}: UsePolygonHandleDragParams): UsePolygonHandleDragResult {
  const dispatch = useAppDispatch();
  const sessionRef = useRef<PolygonDragSession | null>(null);
  const cleanupRef = useRef<(() => void) | null>(null);
  const [hoverState, setHoverState] = useState<PolygonHandleHoverState>({
    vertexIndex: null,
    edgeIndex: null
  });
  const [activeState, setActiveState] = useState<PolygonHandleActiveState>({
    vertexIndex: null,
    edgeIndex: null
  });

  useEffect(() => {
    return () => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
  }, []);

  const setHoverVertex = useCallback((vertexIndex: number | null) => {
    setHoverState((prev) => ({ ...prev, vertexIndex }));
  }, []);

  const setHoverEdge = useCallback((edgeIndex: number | null) => {
    setHoverState((prev) => ({ ...prev, edgeIndex }));
  }, []);

  const applyCandidate = useCallback(
    (positionFeet: PositionType, verticesFeet: RelativeVertex[]) => {
      const draft: {
        vertices: RelativeVertex[];
        width?: number;
        length?: number;
      } = {
        vertices: verticesFeet
      };
      assignSpacePolygonShape(draft, verticesFeet);

      dispatch(
        updateStructureItem({
          uniqueId: gridSpace.spaceId,
          position: { x: positionFeet.x, y: positionFeet.y },
          width: draft.width,
          length: draft.length,
          vertices: draft.vertices
        } as PartialWithRequired<SpaceType, 'uniqueId'>)
      );

      dispatch(
        updateStructureItemAttribute({
          uniqueId: gridSpace.spaceId,
          attributeCode: 'square_footage',
          value: Math.round(polygonArea(draft.vertices ?? verticesFeet))
        })
      );
    },
    [dispatch, gridSpace.spaceId]
  );

  const isCandidateValid = useCallback(
    (
      candidateVerticesFeet: RelativeVertex[],
      candidatePositionFeet: PositionType
    ): boolean => {
      if (candidateVerticesFeet.length < 3) {
        return false;
      }

      if (!isPolygonSimple(candidateVerticesFeet)) {
        return false;
      }

      const absVerticesFeet = verticesToAbsolute(
        candidateVerticesFeet,
        candidatePositionFeet
      );

      if (!isPolygonInsideAnyFootprint(absVerticesFeet, footprints)) {
        return false;
      }

      const otherPolygons = buildOtherSpacePolygonsAbsolute(
        allGridSpaces,
        gridSpace.spaceId
      );
      for (let i = 0; i < otherPolygons.length; i += 1) {
        if (polygonOverlapsAny(absVerticesFeet, [otherPolygons[i]])) {
          return false;
        }
      }

      const bb = boundingBoxOfVertices(candidateVerticesFeet);
      if (bb.width < MIN_SPACE_DIM_FEET || bb.height < MIN_SPACE_DIM_FEET) {
        return false;
      }

      return true;
    },
    [allGridSpaces, footprints, gridSpace.spaceId]
  );

  const startSession = useCallback(
    (session: PolygonDragSession) => {
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
      sessionRef.current = session;
      setActiveState({
        vertexIndex: session.kind === 'vertex' ? session.vertexIndex : null,
        edgeIndex: session.kind === 'edge-angled' ? session.edgeIndex : null
      });

      dispatch(beginUndoBatch());
      let batchOpen = true;
      const closeBatch = () => {
        if (!batchOpen) return;
        batchOpen = false;
        dispatch(endUndoBatch());
      };

      let lastAppliedDelta: { dx: number; dy: number } | null = null;
      const applyClampedIfChanged = (clamped: ClampedDelta | null) => {
        if (!clamped) return;
        if (
          lastAppliedDelta?.dx === clamped.dx &&
          lastAppliedDelta?.dy === clamped.dy
        ) {
          return;
        }
        lastAppliedDelta = { dx: clamped.dx, dy: clamped.dy };
        applyCandidate(
          clamped.candidate.positionFeet,
          clamped.candidate.vertices
        );
      };

      const handleMouseMove = (moveEvent: MouseEvent) => {
        const { current } = sessionRef;
        if (!current) return;

        const dxPixels =
          (moveEvent.clientX - current.startMousePixels.x) / effectiveZoom;
        const dyPixels =
          (moveEvent.clientY - current.startMousePixels.y) / effectiveZoom;
        const dxFeetRaw = dxPixels / STRUCTURE_GRID_SIZE;
        const dyFeetRaw = dyPixels / STRUCTURE_GRID_SIZE;

        if (current.kind === 'vertex') {
          const targetAbsoluteFeet = {
            x:
              current.basePositionFeet.x +
              current.initialVertexFeet.x +
              dxFeetRaw,
            y:
              current.basePositionFeet.y +
              current.initialVertexFeet.y +
              dyFeetRaw
          };

          const snapTarget = findNearestNeighborVertexFeet(
            targetAbsoluteFeet,
            current.neighborPolygonsAbsoluteFeet
          );

          if (snapTarget) {
            const snapDx = Math.round(
              snapTarget.x -
                (current.basePositionFeet.x + current.initialVertexFeet.x)
            );
            const snapDy = Math.round(
              snapTarget.y -
                (current.basePositionFeet.y + current.initialVertexFeet.y)
            );
            if (snapDx !== 0 || snapDy !== 0) {
              const snapCandidate = buildVertexCandidate(
                current.baseVerticesFeet,
                current.vertexIndex,
                current.basePositionFeet,
                {
                  x: current.initialVertexFeet.x + snapDx,
                  y: current.initialVertexFeet.y + snapDy
                }
              );
              const accepted = isCandidateValid(
                snapCandidate.vertices,
                snapCandidate.positionFeet
              );
              if (accepted) {
                applyClampedIfChanged({
                  dx: snapDx,
                  dy: snapDy,
                  candidate: snapCandidate
                });
                return;
              }
            }
          }

          const requestedDx = Math.round(dxFeetRaw);
          const requestedDy = Math.round(dyFeetRaw);
          const clamped = findMaxValidVertexDelta(
            current.baseVerticesFeet,
            current.vertexIndex,
            current.initialVertexFeet,
            current.basePositionFeet,
            requestedDx,
            requestedDy,
            isCandidateValid
          );
          applyClampedIfChanged(clamped);
        } else if (current.kind === 'edge-angled') {
          let requestedDx = 0;
          let requestedDy = 0;
          if (current.axis === 'horizontal') {
            requestedDx = Math.round(dxFeetRaw);
          } else {
            requestedDy = Math.round(dyFeetRaw);
          }
          const clamped = findMaxValidEdgeDelta(
            current.baseVerticesFeet,
            current.edgeIndex,
            current.basePositionFeet,
            requestedDx,
            requestedDy,
            isCandidateValid
          );
          applyClampedIfChanged(clamped);
        }
      };

      const handleMouseUp = () => {
        sessionRef.current = null;
        setActiveState({ vertexIndex: null, edgeIndex: null });
        closeBatch();
        if (cleanupRef.current) {
          cleanupRef.current();
          cleanupRef.current = null;
        }
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);

      cleanupRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        closeBatch();
      };
    },
    [applyCandidate, dispatch, effectiveZoom, isCandidateValid]
  );

  const onVertexMouseDown = useCallback(
    (e: React.MouseEvent, vertexIndex: number) => {
      const verticesPixels = gridSpace.vertices ?? [];
      if (verticesPixels.length < 3) return;
      if (vertexIndex < 0 || vertexIndex >= verticesPixels.length) return;

      const verticesFeet = pixelVerticesToFeet(verticesPixels);
      const widthFeet = gridSpace.position.width / STRUCTURE_GRID_SIZE;
      const heightFeet = gridSpace.position.height / STRUCTURE_GRID_SIZE;

      if (isRectangularVertices(verticesFeet, widthFeet, heightFeet)) {
        const corner = getBboxCornerForVertex(
          verticesFeet,
          vertexIndex,
          widthFeet,
          heightFeet
        );
        if (corner) {
          beginCornerResize(e, corner);
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();

      const basePositionFeet = {
        x: gridSpace.position.x / STRUCTURE_GRID_SIZE,
        y: gridSpace.position.y / STRUCTURE_GRID_SIZE
      };

      const neighborPolygonsAbsoluteFeet = buildOtherSpacePolygonsAbsolute(
        allGridSpaces,
        gridSpace.spaceId
      );

      const session: VertexDragSession = {
        kind: 'vertex',
        vertexIndex,
        startMousePixels: { x: e.clientX, y: e.clientY },
        initialVertexFeet: verticesFeet[vertexIndex],
        baseVerticesFeet: verticesFeet,
        basePositionFeet,
        neighborPolygonsAbsoluteFeet
      };
      startSession(session);
    },
    [
      allGridSpaces,
      beginCornerResize,
      gridSpace.position.height,
      gridSpace.position.width,
      gridSpace.position.x,
      gridSpace.position.y,
      gridSpace.spaceId,
      gridSpace.vertices,
      startSession
    ]
  );

  const onEdgeMouseDown = useCallback(
    (e: React.MouseEvent, edgeIndex: number) => {
      const verticesPixels = gridSpace.vertices ?? [];
      if (verticesPixels.length < 3) return;
      if (edgeIndex < 0 || edgeIndex >= verticesPixels.length) return;

      const verticesFeet = pixelVerticesToFeet(verticesPixels);
      const axisAligned = isEdgeAxisAligned(verticesFeet, edgeIndex);

      if (axisAligned && verticesPixels.length === 4) {
        const cardinal = getCardinalFromEdge(verticesFeet, edgeIndex);
        if (cardinal) {
          beginEdgeResize(e, cardinal);
          return;
        }
      }

      e.preventDefault();
      e.stopPropagation();

      const axis: AngledEdgeDragAxis = axisAligned
        ? edgeOrientationAxis(verticesFeet, edgeIndex)
        : angledEdgeDragAxis(verticesFeet, edgeIndex);
      const basePositionFeet = {
        x: gridSpace.position.x / STRUCTURE_GRID_SIZE,
        y: gridSpace.position.y / STRUCTURE_GRID_SIZE
      };

      const session: AngledEdgeDragSession = {
        kind: 'edge-angled',
        edgeIndex,
        axis,
        startMousePixels: { x: e.clientX, y: e.clientY },
        baseVerticesFeet: verticesFeet,
        basePositionFeet
      };
      startSession(session);
    },
    [
      beginEdgeResize,
      gridSpace.position.x,
      gridSpace.position.y,
      gridSpace.vertices,
      startSession
    ]
  );

  return {
    onVertexMouseDown,
    onEdgeMouseDown,
    hoverState,
    activeState,
    setHoverVertex,
    setHoverEdge
  };
}
